import React, { useCallback, useEffect, useState } from 'react';
import styles from './styles.module.css';
import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import IconAdd from '@mui/icons-material/Add';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import CustomButton from '../../../../components/buttons/CustomButton';
import AttachmentList from '../../../../components/patient-attachments/AttachmentList';
import AttachmentViewerPopup from '../../../../components/patient-attachments/AttachmentViewerPopup';
import PatientAttachmentUploadPopup from '../../../../popup/PatientAttachmentUploadPopup/PatientAttachmentUploadPopup';
import { ATTACHMENT_CATEGORIES, CATEGORY_OTHER } from '../../../../components/patient-attachments/attachmentCategories';
import { DataProviderInstance } from '../../../../api/DataProvider';

const CONTEXT_GENERAL = 'general';

/**
 * Card "Arquivos": anexos genéricos do paciente (exames, laudos, receitas).
 * Mesmo contrato dos cards irmãos do atendimento. Quando `list` não vem da
 * página (ficha do paciente), o card carrega os anexos sozinho. Depois de
 * qualquer alteração ele se atualiza via list.php, sem recarregar a página.
 */
const PatientAttachments = ({ list, patientId, appointmentId, currentDate, onUpdated, enabled = false, collapsible = false }) => {
    const { ListPatientAttachments } = DataProviderInstance();

    const selfLoaded = list === undefined;

    const [rows, setRows] = useState(list || []);
    const [showUpload, setShowUpload] = useState(false);
    const [selected, setSelected] = useState(null);

    const loadList = useCallback(() => {
        if (!patientId) return;

        ListPatientAttachments(patientId, CONTEXT_GENERAL, (response) => {
            if (response && response.status === 'SUCCESS') {
                setRows(response.data || []);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    useEffect(() => {
        if (selfLoaded) {
            loadList();
        } else {
            setRows(list || []);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list, patientId]);

    const attachments = (rows || []).filter(item => (item.context || CONTEXT_GENERAL) === CONTEXT_GENERAL);

    /**
     * Após anexar, editar ou excluir, o card recarrega só a própria lista.
     * Nunca pede reload da página: o profissional pode estar com a evolução
     * ou outro texto do atendimento em edição.
     */
    function updatedHandler() {
        loadList();
        if (onUpdated) onUpdated();
    }

    function uploadHideHandler(success) {
        setShowUpload(false);

        if (success) updatedHandler();
    }

    function viewerHideHandler(changed) {
        setSelected(null);

        if (changed) updatedHandler();
    }

    /**
     * Campos por arquivo do popup de upload: categoria (lista fixa + texto
     * livre em "Outro") e título.
     */
    function renderItemFields(file, value, onChange) {
        const current = value || {};

        return (
            <Box className={styles.itemFields}>
                <FormControl size="small" className={styles.itemField}>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                        label="Categoria"
                        value={current.category || ''}
                        onChange={(event) => onChange({ ...current, category: event.target.value })}
                    >
                        {ATTACHMENT_CATEGORIES.map((item) => (
                            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {current.category === CATEGORY_OTHER && (
                    <TextField
                        size="small"
                        label="Qual categoria?"
                        className={styles.itemField}
                        value={current.customCategory || ''}
                        onChange={(event) => onChange({ ...current, customCategory: event.target.value })}
                    />
                )}

                <TextField
                    size="small"
                    label="Título"
                    className={styles.itemField}
                    value={current.title || ''}
                    onChange={(event) => onChange({ ...current, title: event.target.value })}
                />
            </Box>
        );
    }

    function buildItemPayload(value) {
        const current = value || {};

        const category = current.category === CATEGORY_OTHER
            ? (current.customCategory || '').trim()
            : current.category;

        return {
            category: category || '',
            title: (current.title || '').trim()
        };
    }

    return (
        <>
            <ModuleCard
                accent="green"
                title="Arquivos"
                icon={<FolderOutlinedIcon />}
                description="Exames, laudos e outros arquivos do paciente"
                collapsible={collapsible}
                actions={enabled && (
                    <CustomButton
                        variant="contained"
                        style="primary"
                        label="Anexar"
                        icon={<IconAdd />}
                        onClick={() => setShowUpload(true)}
                    />
                )}
            >
                {attachments.length === 0 ? (
                    <ModuleEmptyState icon={<AttachFileOutlinedIcon />} text="Nenhum arquivo anexado." />
                ) : (
                    <AttachmentList list={attachments} onSelect={(item) => setSelected(item)} />
                )}
            </ModuleCard>

            {showUpload && (
                <PatientAttachmentUploadPopup
                    patientId={patientId}
                    appointmentId={appointmentId}
                    context={CONTEXT_GENERAL}
                    defaultDate={currentDate}
                    maxFiles={10}
                    renderItemFields={renderItemFields}
                    buildItemPayload={buildItemPayload}
                    OnHide={uploadHideHandler}
                />
            )}

            {selected && (
                <AttachmentViewerPopup
                    attachment={selected}
                    list={attachments}
                    enabled={enabled}
                    OnHide={viewerHideHandler}
                />
            )}
        </>
    );
};

export default PatientAttachments;
